import React, { useMemo } from 'react';
import { Box, Card, CardActionArea, CardContent, Grid, Link as MuiLink, Typography } from '@mui/material';
import { BarChart as IconBarChart, Sick as IconSick, EscalatorWarning as IconEscalera
    , MonitorHeart as IconMonitor, School as IconEscuela, Schedule as IconCalendario
    , Timer as IconReloj, ArrowForward as IconFlecha, AssignmentInd as IconParticipante
    , TrendingUp as IconTrendingUp, Inventory as IconInventory, AccountBalanceWallet as IconWallet } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useMantenimiento } from '../../hooks/useMantenimiento';
import { puedeAccederFichaIngreso, puedeAccederKardexYCaja } from '../../services/permisoService';
import { obtenerUsuario } from '../../services/authService';

// Portada de los informes: la lista de los que el usuario puede abrir,
// agrupada por tema.
//
// Cada informe vive en su pantalla; aqui solo se muestran como tarjetas, con
// lo que hace cada uno. Se listan todos y se deja ver unicamente los que el
// usuario tiene concedidos, igual que hace el menu.

const GRUPOS = [
  {
    titulo: "Terapias",
    detalle: "Las terapias del centro y lo que se planifica sobre ellas.",
    informes: [
      {
        titulo: "Reporte de Terapias",
        detalle: "Las terapias activas con su participante, terapeuta, horario y salon.",
        ruta: "/reporte/terapia",
        acceso: "terapiaReporte.ver",
        icono: <IconSick />,
      },
      {
        titulo: "Reporte de Planes de Terapia",
        detalle: "El avance de las sesiones por cada area del plan: sesion por sesion, con el grupo de puntuacion del area.",
        ruta: "/reporte/planterapia",
        acceso: "terapiaPlanReporte.ver",
        icono: <IconEscalera />,
      },
      {
        titulo: "Reporte Trimestral",
        detalle: "El sistema anual de intervencion terapeutica del plan trimestral.",
        ruta: "/reporte/trimestral",
        acceso: "terapiaPlanReporte.ver",
        icono: <IconEscalera />,
      },
      {
        titulo: "Curva de Eficacia Terapéutica",
        detalle: "Correlación estadística y línea de tendencia entre sesiones acumuladas y % de logro de objetivos clínicos.",
        ruta: "/reporte/curva-eficacia",
        acceso: "terapiaPlanReporte.ver",
        icono: <IconTrendingUp />,
      },
    ],
  },
  {
    titulo: "Sesiones",
    detalle: "El trabajo registrado por el terapeuta y el tiempo que acumula.",
    informes: [
      {
        titulo: "Resumen de Tiempos",
        detalle: "Las horas de sesion validas por terapeuta y periodo, con el detalle de las sesiones que las suman.",
        ruta: "/planilla",
        acceso: "sesion.ver",
        icono: <IconReloj />,
      },
      {
        titulo: "Reporte de Sesiones",
        detalle: "Las sesiones registradas y validadas por terapeuta, con las horas que acumulan.",
        ruta: "/reporte/sesion",
        acceso: "sesionReporte.ver",
        icono: <IconMonitor />,
      },
      {
        titulo: "Horarios Disponibles",
        detalle: "Los bloques de horario de cada terapeuta, para saber cuando tiene hueco libre.",
        ruta: "/horario-terapeuta",
        acceso: "sesion.ver",
        icono: <IconCalendario />,
      },
    ],
  },
  {
    titulo: "Modelos",
    detalle: "La estructura de intervencion: modelos, areas, objetivos y criterios.",
    informes: [
      {
        titulo: "Reporte de Modelos",
        detalle: "Los modelos de intervencion con sus areas, objetivos y criterios.",
        ruta: "/reporte/modelo",
        acceso: "modeloReporte.ver",
        icono: <IconEscuela />,
      },
    ],
  },
  {
    titulo: "Participantes",
    detalle: "Fichas, registros y documentos oficiales del participante.",
    informes: [
      {
        titulo: "Ficha de Matrícula / Registro",
        detalle: "Formato oficial de ficha de ingreso con datos personales, familiares, autorizaciones y firmas.",
        ruta: "/reporte/ficha-participante",
        acceso: "participante.ver",
        icono: <IconParticipante />,
        validador: (usuario, roles) => puedeAccederFichaIngreso(usuario, roles),
      },
    ],
  },
  {
    titulo: "Administración y Logística",
    detalle: "Control físico y valorizado de inventario, kardex y arqueos de caja chica.",
    informes: [
      {
        titulo: "Reporte de Kardex e Inventario",
        detalle: "Kardex valorizado permanente (método promedio ponderado), entradas y salidas de materiales por sede.",
        ruta: "/inventario/reportes",
        acceso: "kardex.reporte",
        icono: <IconInventory />,
        validador: (usuario, roles) => puedeAccederKardexYCaja(usuario, roles),
      },
      {
        titulo: "Arqueo y Liquidación de Caja Chica",
        detalle: "Estado de fondos fijos, liquidación periódica y clasificación de comprobantes de gasto por sede.",
        ruta: "/cajachica/reportes",
        acceso: "cajachica.reporte",
        icono: <IconWallet />,
        validador: (usuario, roles) => puedeAccederKardexYCaja(usuario, roles),
      },
    ],
  },
];

const Index = () => {
  useMantenimiento();
  const accesos = useSelector((state) => state.seguridad?.accesos) || [];
  const roles = useSelector((state) => state.seguridad?.roles) || [];
  const seguridadActiva = useSelector((state) => state.seguridad?.seguridadActiva) || "N";
  const usuario = obtenerUsuario();

  const grupos = useMemo(() => {
    return GRUPOS
      .map((grupo) => ({
        ...grupo,
        informes: grupo.informes.filter((informe) => {
          if (informe.validador && !informe.validador(usuario, roles)) {
            return false;
          }
          if (seguridadActiva !== "S") return true;
          return accesos.includes(informe.acceso);
        }),
      }))
      .filter((grupo) => grupo.informes.length > 0);
  }, [accesos, seguridadActiva, roles, usuario]);

  return (
    <Box>
      {/* Cabecera */}
      <Box sx={{
        background: "linear-gradient(120deg, #1976d2 0%, #5b2d8e 100%)",
        color: "white",
        borderRadius: 1,
        padding: { xs: 2.5, sm: 3.5 },
        marginBottom: 3,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconBarChart sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              Reportes
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              La informacion del sistema lista para revisar, imprimir o llevar a Excel, Word o PDF.
            </Typography>
          </Box>
        </Box>
      </Box>

      {grupos.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: "center", padding: 6 }}>
            <Typography color="text.secondary">
              No tienes reportes disponibles. Los reportes se conceden por rol desde la configuracion de accesos.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        grupos.map((grupo) => (
          <Box key={grupo.titulo} sx={{ marginBottom: 4 }}>
            {/* Encabezado del tema */}
            <Box sx={{ marginBottom: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                {grupo.titulo}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {grupo.detalle}
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {grupo.informes.map((informe) => (
                <Grid item xs={12} sm={6} md={4} key={informe.ruta}>
                  <Card variant="outlined" sx={{ height: "100%", "&:hover": { borderColor: "#5b2d8e" } }}>
                    <CardActionArea
                      component={Link}
                      to={informe.ruta}
                      sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "stretch" }}
                    >
                      <CardContent>
                        <Box sx={{
                          width: 44, height: 44, borderRadius: "12px",
                          backgroundColor: "#f3edf9", color: "#5b2d8e",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          marginBottom: 2,
                        }}>
                          {informe.icono}
                        </Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                          {informe.titulo}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1.5 }}>
                          {informe.detalle}
                        </Typography>
                        <MuiLink component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5, fontWeight: "bold" }}>
                          Ver reporte <IconFlecha fontSize="small" />
                        </MuiLink>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))
      )}
    </Box>
  )
}

export default Index;
